import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {PopoverDirective} from 'ngx-bootstrap/popover';

import {Address} from '../../route-planner/planner/shared/models/address.model';
import {OffsetService} from "../../route-planner/planner/shared/services/offset/offset.service";
import {MatFormField} from "@angular/material/form-field";

@Component({
    selector: 'app-form-input-autocomplete',
    templateUrl: './form-input-autocomplete.component.html',
    styleUrls: ['./form-input-autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormInputAutocompleteComponent),
            multi: true
        }
    ],
    standalone: false
})
export class FormInputAutocompleteComponent implements ControlValueAccessor, OnDestroy, OnInit {
    private readonly translateService = inject(TranslateService);
    private readonly offsetService = inject(OffsetService);

    public get value(): Address {
        return this._value;
    }

    @Input()
    public set value(value: string | Address) {
        if (typeof value === 'string') {
            this._value.streetNameAndHouseNumber = value;
            this._value.userSelectedOption = false;
        } else {
            this._value = value;
            this._value.userSelectedOption = true;
        }

        this._onChange(this._value);
        this._onTouched(this._value);
    }

    public get error() {
        return this._error;
    }

    @Input()
    public set error(value: string) {
        this._error = value;
        this.popoverDirective.popover = '';
        this.popoverDirective.placement = this.errorPosition;
        this.popoverDirective.hide();

        if (value) {
            this.popoverDirective.popover = this.translateService.instant(value);
            this.popoverDirective.show();
        }
    }

    @Input() type: string;
    @Input() placeholder: string;
    @Input() disable: boolean;
    @Input() readonly: boolean;

    @Input() addressSuggestions: Address[];
    @Input() searching: boolean;

    @Input()
    public set errorVisual(value: string) {
        this._error = value;
    }

    @Input() errorPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';
    @Output() changed = new EventEmitter();
    @Output() opened = new EventEmitter();

    private _value: Address;
    private _error: string;
    private observer: MutationObserver;

    @ViewChild(PopoverDirective, {static: true}) private readonly popoverDirective: PopoverDirective;
    @ViewChild(MatFormField, {static: true}) private formField: MatFormField;

    ngOnInit() {
        this.observer = new MutationObserver(() => {
            this.calculateOffset();
        });
    }

    ngOnDestroy() {
        this.observer.disconnect();
    }

    public registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    public writeValue(value: Address): void {
        if (value !== null) {
            this._value = value;
        }
    }

    public onChange(newValue: any): void {
        this.changed.emit(newValue);

    }

    public setDisabledState(isDisabled: boolean) {
        this.addressSuggestions = null;
        this.disable = isDisabled;
    }

    public displayAddress(address: Address): string {
        if (!address) {
            return null;
        }

        if (address && address.streetNameAndHouseNumber) {
            return address.streetNameAndHouseNumber;
        }

        return '';
    }

    public displayAddressAndZipCode(address: Address): string {
        let displayString = '';
        if (!address) {
            return displayString;
        }

        if (address.streetNameAndHouseNumber) {
            displayString += address.streetNameAndHouseNumber;
        }

        if (address.zipCode) {
            displayString += `, ${address.zipCode}`;
        }
        return displayString;
    }

    public onOpen() {
        const offsetElement = document.querySelector(".cdk-overlay-pane") as HTMLElement;
        this.observer.observe(offsetElement, {
            attributes: true,
            attributeFilter: ['style', 'transform']
        });
        this.calculateOffset();
    }

    public onClose() {
        this.observer.disconnect();
    }

    private _onChange: any = () => {
    };
    private _onTouched: any = () => {
    };

    private calculateOffset(): void {
        setTimeout(() => {
            this.offsetService.setOffset(".cdk-overlay-pane", this.formField._elementRef.nativeElement as HTMLElement);
        });
    }
}
